<template>
  <div v-if="sections.length" class="release-details">
    <section v-for="section in sections" :key="section.key" class="release-details__section">
      <h4 v-if="section.title" class="release-details__heading">{{ section.title }}</h4>
      <ul class="release-details__list">
        <li v-for="item in section.items" :key="item.key" class="release-details__item">
          <v-icon class="release-details__bullet" icon="mdi-check" size="16" color="primary" />
          <p>
            <span class="release-details__term">{{ labelOf(item.term) }}</span>
            {{ item.text }}
          </p>
        </li>
      </ul>
    </section>
  </div>
</template>

<script setup lang="ts">
/**
 * @fileOverview The few points behind a release highlight's two sentences, shared by the dialog and the
 * changelog: a plain list, or one under a heading where the points belong together.
 *
 * Always open, and short on purpose: behind a click it is the part nobody reads, and a long list in the
 * open is the part nobody finishes. See docs/release-notes.md.
 */
defineProps<{
  sections: { key: string; title: string; items: { key: string; term: string; text: string }[] }[]
}>()

/** The term as a lead-in: „Abrechnungseinheit:“, but „Position fehlt?“ keeps its question mark alone. */
const labelOf = (term: string) => (/[?!:.]$/.test(term) ? term : `${term}:`)
</script>

<style scoped>
.release-details {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-top: 14px;
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.release-details__heading {
  margin-bottom: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.release-details__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.release-details__item {
  display: grid;
  grid-template-columns: 16px minmax(0, 1fr);
  gap: 10px;
  font-size: 0.875rem;
  line-height: 1.5;
}

.release-details__item p {
  margin: 0;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.release-details__bullet {
  margin-top: 2px;
}

.release-details__term {
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
}
</style>
